import React, { useEffect } from "react";

const APP_STORE_URL = "https://apps.apple.com/";

type Screenshot = {
  src: string;
  alt: string;
};

const portraitScreenshots: Screenshot[] = [
  {
    src: "/momental/assets/momental-app-store-iphone-6.5.png",
    alt: "Momental App Store artwork for a 6.5-inch iPhone display",
  },
  { src: "/momental/assets/screenshot-main-menu.png", alt: "Momental main menu" },
  { src: "/momental/assets/screenshot-one-way-walls.png", alt: "One-way walls puzzle" },
  { src: "/momental/assets/screenshot-tunnels.png", alt: "Tunnels mechanic" },
  { src: "/momental/assets/screenshot-rotating-tiles.png", alt: "Rotating tiles puzzle" },
  { src: "/momental/assets/screenshot-rotating-tunnels.png", alt: "Rotating tunnels" },
  { src: "/momental/assets/screenshot-portals.png", alt: "Portals puzzle" },
  { src: "/momental/assets/screenshot-color-rush-replay-mode.png", alt: "Color Rush replay mode" },
];

const landscapeScreenshot: Screenshot = {
  src: "/momental/assets/screenshot-level-editor.png",
  alt: "In-game level editor",
};

const keyFeatures = [
  "Momentum-based sliding puzzles on a hexagonal grid",
  "Rotating tiles, tunnels, portals, and other mechanics that redirect the ball's path",
  "200+ handcrafted levels across 9 puzzle worlds",
  "Replay challenges that add new objectives to every level",
  "In-game level editor and community hub for creating and sharing puzzles",
  "Original soundtrack with adaptive music that evolves as puzzles progress",
];

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #fbfbfc 0%, #f6f7fb 60%, #fafafa 100%)",
    color: "#222",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  } as React.CSSProperties,

  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "48px 24px 64px",
    lineHeight: 1.7,
  } as React.CSSProperties,

  section: {
    marginBottom: "56px",
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#2c5282",
    margin: "0 0 20px 0",
    paddingBottom: "10px",
    borderBottom: "1px solid #cbd5e0",
  } as React.CSSProperties,

  videoContainer: {
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto",
    aspectRatio: "9 / 19.5",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#000",
    marginBottom: "16px",
  } as React.CSSProperties,

  landscapeVideoContainer: {
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#000",
    marginBottom: "16px",
  } as React.CSSProperties,

  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  } as React.CSSProperties,

  videoLabel: {
    textAlign: "center" as const,
    color: "#666",
    fontSize: "0.9rem",
    marginTop: "8px",
  } as React.CSSProperties,

  heroSection: {
    marginBottom: "48px",
  } as React.CSSProperties,

  heroTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap" as const,
    gap: "32px",
  } as React.CSSProperties,

  heroArtwork: {
    width: "min(100%, 470px)",
    height: "auto",
    borderRadius: "8px",
    boxShadow: "0 20px 46px rgba(15,23,42,0.16)",
    flex: "1 1 440px",
  } as React.CSSProperties,

  heroCopy: {
    flex: "1 1 320px",
    minWidth: 0,
  } as React.CSSProperties,

  title: {
    fontSize: "2.5rem",
    fontWeight: 800,
    color: "#111",
    margin: 0,
  } as React.CSSProperties,

  subtitle: {
    fontSize: "1.15rem",
    fontWeight: 600,
    color: "#2d3748",
    margin: "12px 0 16px",
    lineHeight: 1.5,
    textAlign: "left" as const,
  } as React.CSSProperties,

  tagline: {
    fontSize: "1.05rem",
    color: "#4a5568",
    margin: 0,
    lineHeight: 1.6,
    textAlign: "left" as const,
  } as React.CSSProperties,

  featureList: {
    margin: 0,
    paddingLeft: "24px",
    color: "#2d3748",
    fontSize: "1.05rem",
  } as React.CSSProperties,

  featureItem: {
    marginBottom: "8px",
  } as React.CSSProperties,

  screenshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    alignItems: "start",
    gap: "16px",
  } as React.CSSProperties,

  landscapeScreenshotCard: {
    gridColumn: "1 / -1",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  } as React.CSSProperties,

  screenshotCard: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  } as React.CSSProperties,

  screenshotImg: {
    width: "100%",
    height: "auto",
    display: "block",
  } as React.CSSProperties,

  assetList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  } as React.CSSProperties,

  assetItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 0",
    borderBottom: "1px solid #edf2f7",
  } as React.CSSProperties,

  assetDownloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2b6cb0",
    color: "#fff",
    padding: "6px 12px",
    borderRadius: "6px",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    flexShrink: 0,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,

  assetLabel: {
    color: "#2d3748",
    fontSize: "0.95rem",
  } as React.CSSProperties,

  factSheet: {
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "24px",
  } as React.CSSProperties,

  factRow: {
    display: "flex",
    borderBottom: "1px solid #edf2f7",
    padding: "12px 0",
  } as React.CSSProperties,

  factLabel: {
    fontWeight: 600,
    color: "#4a5568",
    width: "140px",
    flexShrink: 0,
  } as React.CSSProperties,

  factValue: {
    color: "#2d3748",
    flex: 1,
  } as React.CSSProperties,

  aboutSection: {
    backgroundColor: "#f7fafc",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
  } as React.CSSProperties,

  aboutTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#2c5282",
    margin: "0 0 12px 0",
  } as React.CSSProperties,

  aboutText: {
    margin: 0,
    color: "#2d3748",
    fontSize: "1.02rem",
    lineHeight: 1.7,
  } as React.CSSProperties,

  contactSection: {
    textAlign: "center" as const,
    padding: "32px",
    backgroundColor: "#ebf8ff",
    borderRadius: "12px",
  } as React.CSSProperties,

  contactTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#2c5282",
    margin: "0 0 12px 0",
  } as React.CSSProperties,

  contactEmail: {
    color: "#2b6cb0",
    fontSize: "1.1rem",
    textDecoration: "none",
    fontWeight: 600,
  } as React.CSSProperties,

  footer: {
    marginTop: "48px",
    paddingTop: "24px",
    borderTop: "2px solid #e0e0e0",
    textAlign: "center" as const,
  } as React.CSSProperties,

  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexWrap: "wrap",
  } as React.CSSProperties,

  footerLink: {
    color: "#2b6cb0",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
  } as React.CSSProperties,

  appStoreBadge: {
    cursor: "pointer",
    height: "48px",
    width: "auto",
  } as React.CSSProperties,
};

const MomentalPressKit: React.FC = () => {
  useEffect(() => {
    document.title = "Momental Press Kit";
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Hero artwork + press-kit introduction */}
        <section style={styles.heroSection}>
          <div style={styles.heroTop}>
            <img
              src="/momental/assets/Momental Logo.png"
              alt="Momental game artwork showing the logo, puzzle board, and gameplay screenshots"
              style={styles.heroArtwork}
            />
            <div style={styles.heroCopy}>
              <h1 style={styles.title}>Press Kit</h1>
              <p style={styles.subtitle}>
                A momentum-driven puzzle game of sliding ball mazes and chain-reaction mechanics.
              </p>
              <p style={styles.tagline}>
                Momental is a momentum-based puzzle game where a rolling ball slides through mazes on a hexagonal grid, bouncing off walls and triggering portals, tunnels, and rotating mechanisms that redirect its path.
              </p>
            </div>
          </div>
        </section>

        {/* 30s Gameplay Video */}
        <section style={styles.section}>
          <div style={styles.videoContainer}>
            <video
              style={styles.video}
              controls
              poster="/momental/assets/video-poster.png"
            >
              <source src="/momental/assets/gameplay-30s.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p style={styles.videoLabel}>30-second gameplay preview</p>
        </section>

        {/* Key Features */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Key Features</h2>
          <ul style={styles.featureList}>
            {keyFeatures.map((feature, idx) => (
              <li key={idx} style={styles.featureItem}>
                {feature}
              </li>
            ))}
          </ul>
        </section>

        {/* 90s Gameplay Video */}
        <section style={styles.section}>
          <div style={styles.videoContainer}>
            <video
              style={styles.video}
              controls
              poster="/momental/assets/video-poster.png"
            >
              <source src="/momental/assets/gameplay-90s.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p style={styles.videoLabel}>90-second extended gameplay</p>
        </section>

        {/* Download on the App Store */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Get the Game</h2>
          <a
            href="https://apps.apple.com/app/id6745159953"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
              alt="Download on the App Store"
              style={styles.appStoreBadge}
            />
          </a>
        </section>

        {/* Screenshots */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Screenshots</h2>
          <div style={styles.screenshotGrid}>
            {portraitScreenshots.map((s, idx) => (
              <div key={idx} style={styles.screenshotCard}>
                <img src={s.src} alt={s.alt} style={styles.screenshotImg} />
              </div>
            ))}
            <div style={styles.landscapeScreenshotCard}>
              <img src={landscapeScreenshot.src} alt={landscapeScreenshot.alt} style={styles.screenshotImg} />
            </div>
          </div>
        </section>

        {/* Press Assets */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Press Assets</h2>
          <ul style={styles.assetList}>
            <li style={styles.assetItem}>
              <a href="/momental/assets/all-press-assets.zip" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>All Press Assets (ZIP)</span>
            </li>
            <li style={styles.assetItem}>
              <a href="/momental/assets/Momental Logo.png" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>Game Logo</span>
            </li>
            <li style={styles.assetItem}>
              <a href="/momental/assets/momental-icon.png" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>App Icon</span>
            </li>
            <li style={styles.assetItem}>
              <a href="/momental/assets/screenshots.zip" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>Screenshots (ZIP)</span>
            </li>
            <li style={styles.assetItem}>
              <a href="/momental/assets/gameplay-30s.mp4" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>30s Gameplay Video</span>
            </li>
            <li style={styles.assetItem}>
              <a href="/momental/assets/gameplay-90s.mp4" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>90s Gameplay Video</span>
            </li>
            <li style={{ ...styles.assetItem, borderBottom: "none" }}>
              <a href="/momental/assets/momental-fact-sheet.pdf" download style={styles.assetDownloadBtn}>Download</a>
              <span style={styles.assetLabel}>Press Fact Sheet (PDF)</span>
            </li>
          </ul>
        </section>

        {/* Fact Sheet */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Fact Sheet</h2>
          <div style={styles.factSheet}>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Title</span>
              <span style={styles.factValue}>Momental</span>
            </div>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Developer</span>
              <span style={styles.factValue}>Luke White</span>
            </div>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Genre</span>
              <span style={styles.factValue}>Puzzle</span>
            </div>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Platforms</span>
              <span style={styles.factValue}>iOS</span>
            </div>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Release Date</span>
              <span style={styles.factValue}>Spring 2026</span>
            </div>
            <div style={styles.factRow}>
              <span style={styles.factLabel}>Price</span>
              <span style={styles.factValue}>
                Free to play 45 levels, $4.99 to unlock all 200+ levels, community hub, custom ball skins
              </span>
            </div>
            <div style={{ ...styles.factRow, borderBottom: "none" }}>
              <span style={styles.factLabel}>Description</span>
              <span style={styles.factValue}>
                Momental is a momentum-based puzzle game where players slide a rolling ball through hexagonal mazes. Walls, rotating tiles, tunnels, and portals redirect the ball's movement, requiring players to plan routes several moves ahead. With more than 200 handcrafted puzzles across nine worlds, Momental focuses on clean logic design, satisfying chain reactions, and spatial reasoning.
              </span>
            </div>
          </div>
        </section>

        {/* About the Developer */}
        <section style={styles.section}>
          <div style={styles.aboutSection}>
            <h2 style={styles.aboutTitle}>About the Developer</h2>
            <p style={styles.aboutText}>
              Luke White is a psychiatrist and independent game developer based in San Francisco. Self-taught in programming, design, and music, he began creating puzzle games three years ago. Momental is his second game, following the daily word puzzle CraftWord.
            </p>
          </div>
        </section>

        {/* Contact */}
        <section style={styles.contactSection}>
          <h2 style={styles.contactTitle}>Contact</h2>
          <a
            href="mailto:MomentalPress@gmail.com"
            style={styles.contactEmail}
          >
            MomentalPress@gmail.com
          </a>
        </section>

        {/* Footer Navigation */}
        <footer style={styles.footer}>
          <div style={styles.footerLinks}>
            <a href="https://momental.craftword.game" style={styles.footerLink}>
              Home
            </a>
            <a href="https://momental.craftword.game/Support" style={styles.footerLink}>
              Support
            </a>
            <a href="https://momental.craftword.game/Privacy" style={styles.footerLink}>
              Privacy
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MomentalPressKit;
